import { vec3 } from 'gl-matrix';
import { NavigationSystem } from '../navigation-mesh';
import { SpatialQueryGrid } from '../spatial';
import { TickEventManager } from '../util';
import { PhysicsBody, PhysicsFeature } from '../physics';
import { Agent } from '../spatial/agents/Agent';
import { AgentRegistry } from '../spatial/agents/AgentRegistry';
import { NavigationAwareObject } from './NavigationAwareObject';

export interface NavigationObjectDeps {
  navigation: NavigationSystem;
  spatialGrid: SpatialQueryGrid | null;
  tickEventManager: TickEventManager;
  physics?: PhysicsFeature;
  /**
   * Dynamic-agent registry — handed through to `NavigationAwareObject`'s
   * `navigationData` so behaviours can query nearby agents. The caller is
   * responsible for registering this entity's own agent (see `selfAgent`)
   * because that requires a `RenderObject` reference, which this helper
   * intentionally doesn't take (it accepts a structural `{ position }`
   * to keep tests / headless contexts cheap).
   */
  agentRegistry?: AgentRegistry | null;
  /**
   * This entity's own agent, when registered. Passed to registry queries
   * as `self` so we don't body-block against ourselves.
   */
  selfAgent?: Agent | null;
}

export function createNavigationAwareObject(
  deps: NavigationObjectDeps,
  obj: { position: vec3 | number[] },
  speed: number,
): NavigationAwareObject {
  // TODO: keep track of nav objects and dispose them when required
  let nav = new NavigationAwareObject(
    obj,
    {
      navigation: deps.navigation,
      spatialGrid: deps.spatialGrid,
      agentRegistry: deps.agentRegistry ?? null,
      selfAgent: deps.selfAgent ?? null,
    },
    speed,
  );

  // Path-walked movement (the wandering / follow-target flow that goes through
  // `TimedMovement`) doesn't know about other agents — A* routes through nav
  // nodes only and the per-step `travel()` just writes the next position. To
  // keep NPCs from walking through each other and through the player, wrap the
  // step with a save-check-restore: if the new position would overlap any
  // other agent within `selfAgent`'s capsule sweep, undo this frame's motion.
  // The wandering behaviour's idle/retry loop naturally repaths around the
  // stall — no replanning logic needed here.
  const reg = deps.agentRegistry ?? null;
  const selfAgent = deps.selfAgent ?? null;
  if (reg && selfAgent) {
    const oldXZ = vec3.create();
    deps.tickEventManager.registerFrameEvent(() => {
      const pos = obj.position as vec3;
      oldXZ[0] = pos[0];
      oldXZ[1] = pos[1];
      oldXZ[2] = pos[2];
      nav.movement.step();
      // No XZ movement → nothing to check.
      if (pos[0] === oldXZ[0] && pos[2] === oldXZ[2]) return;
      const hit = reg.sweepHorizontal(oldXZ, pos as vec3, selfAgent.radius, selfAgent);
      if (hit !== null) {
        // Another agent is in our way — restore old XZ. Y stays (the floor
        // snap in `NavigationAwareObject.onStep` already wrote it).
        pos[0] = oldXZ[0];
        pos[2] = oldXZ[2];
      }
    });
  } else {
    deps.tickEventManager.registerFrameEvent(() => {
      nav.movement.step();
    });
  }

  // Initialize entity position to correct floor height
  if (nav.movement.currentNode?.node && deps.spatialGrid) {
    let nodeMaxY = nav.movement.currentNode.node.max[1] as number;
    let h = deps.spatialGrid.getFloorHeightBelow(
      obj.position[0] as number,
      nodeMaxY,
      obj.position[2] as number,
    );
    if (h !== null) {
      obj.position[1] = h;
    }
  }

  if (deps.physics) {
    let body = new PhysicsBody(obj.position, { mode: 'gravity-only' });
    body.surfaceHeight = obj.position[1] as number;
    body.minFloorHeight = obj.position[1] as number;
    body.grounded = true;
    nav.physicsBody = body;
    deps.physics.addBody(body);
  }

  return nav;
}
